import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Theme } from '@/styles/theme';
import { parseNumberInput } from '@/utils/input';
import { getColorStyle } from '@/utils/style/ui';
import InputPanel, {
  Background,
  Props as PropsInputPanel,
} from '../surface/InputPanel';

interface PropsInput extends PropsInputPanel {
  opacity?: keyof Theme['opacity'];
}

export interface Props
  extends React.ComponentPropsWithoutRef<'input'>,
    PropsInput {
  isNumberInput?: boolean;
}

const StyledInput = styled.input<PropsInput>`
  font-size: inherit;
  color: ${({ theme, color }) =>
    getColorStyle({
      theme,
      color,
    }) || theme.colors.text};
  ${({ theme }) => theme.font.bold}
  text-align: ${({ textAlign }) => textAlign ?? 'left'};
  background-color: transparent;
  ${({ theme, opacity }) => theme.opacity[opacity ?? 100]};
  outline: none;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }
  &[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
  &::placeholder {
    font-size: inherit;
    color: ${({ theme }) => theme.colors.secondary};
    ${({ theme }) => theme.font.bold}
    opacity: 0.6;
  }

  &:focus + ${Background} {
    ${({ theme, isDisabled, isReadonly }) =>
      isDisabled || isReadonly ? '' : `border-color: ${theme.colors.active};`}
    ${({ theme, isDisabled, isReadonly }) =>
      isDisabled || isReadonly ? '' : theme.bg.surfaceHover}
  }
  & ~ *:not(${Background}) {
    position: relative;
    z-index: 1;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
  ${({ isDisabled, isReadonly }) =>
    isDisabled || isReadonly
      ? `
          cursor: default;
          pointer-events: none;
        `
      : ''}
`;

export default React.forwardRef(function Input(
  {
    type,
    height = 48,
    isNumberInput,
    isDisabled,
    isReadonly,
    padding = 'xs',
    placeholder,
    onChange,
    ...props
  }: Props,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  // ref
  const inputRef = useRef<HTMLInputElement | null>(null);

  // callback
  const handleClick = useCallback(() => {
    inputRef?.current?.focus?.();
  }, [inputRef]);
  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLInputElement>) => {
      try {
        (event.target as HTMLElement).blur();
      } catch (error) {}
    },
    []
  );
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      isDisabled || isReadonly
        ? undefined
        : onChange?.(
            isNumberInput
              ? {
                  ...event,
                  target: {
                    ...event.target,
                    value: parseNumberInput(event.target.value),
                  },
                }
              : event
          ),
    [isDisabled, isReadonly, onChange, isNumberInput]
  );

  return (
    <InputPanel
      bg={isDisabled ? 'surfaceHover' : undefined}
      {...props}
      isDisabled={isDisabled}
      isReadonly={isReadonly}
      height={height}
      padding={padding}
      cursor="text"
      onClick={handleClick}
    >
      <StyledInput
        {...props}
        disabled={isDisabled}
        readOnly={isReadonly}
        isDisabled={isDisabled}
        isReadonly={isReadonly}
        type={type}
        height={height}
        placeholder={placeholder}
        ref={ref ?? inputRef}
        onWheel={handleWheel}
        onChange={handleChange}
      />
    </InputPanel>
  );
});
